// hooks/useDivingData.js
import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext"; // Adjust path as needed

export const useDivingData = () => {
  const [certifications, setCertifications] = useState([]);
  const [recentDives, setRecentDives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { currentUser, userObjectId, loading: authLoading } = useAuth();

  // Create axios instance with interceptor for auth headers
  const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || "/",
  });

  // Add request interceptor to include Bearer token
  apiClient.interceptors.request.use(
    async (config) => {
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error("Error getting Firebase token:", error);
          // Set an error state if token acquisition fails
          setError("Failed to get authentication token.");
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for error handling
  apiClient.interceptors.response.use(
    (response) => response,
    (err) => {
      console.error("API call error:", err);
      // Set specific error messages based on response status
      if (err.response?.status === 401) {
        setError("Authentication failed. Please log in again.");
      } else if (err.response?.status === 403) {
        setError("Access denied. You don't have permission to view this data.");
      } else if (err.response?.status === 404) {
        setError("Diving data not found. Please check your profile setup.");
      } else {
        setError("Failed to load data. Please try again.");
      }
      return Promise.reject(err);
    }
  );

  const fetchDivingData = async () => {
    if (!userObjectId) {
      setError("User ID not available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null); // Clear previous errors

      // Fetch certifications and recent dives in parallel
      const [certsResponse, divesResponse] = await Promise.all([
        apiClient.get(`/api/certifications/diver/${userObjectId}`),
        apiClient.get(`/api/dives/user/${userObjectId}`, {
          params: { months: 12 },
        }),
      ]);

      setCertifications(certsResponse.data);
      setRecentDives(divesResponse.data);
    } catch (err) {
      // The interceptor already handles setting the error state for HTTP errors.
      // This catch block is primarily for network errors or other unexpected issues
      // not caught by the interceptor.
      console.error("Caught error in fetchDivingData:", err);
      if (!error) {
        // Only set a generic error if no specific error was set by interceptor
        setError("Failed to load diving credentials. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch data only when authentication is loaded and user info is available
    if (!authLoading && currentUser && userObjectId) {
      fetchDivingData();
    } else if (!authLoading && !currentUser) {
      setError("Please log in to view diving credentials");
      setLoading(false);
    } else if (!authLoading && currentUser && !userObjectId) {
      setError("User profile not found. Please complete your registration.");
      setLoading(false);
    }
  }, [authLoading, currentUser, userObjectId]); // Re-run effect when auth state changes

  return {
    certifications,
    recentDives,
    loading,
    error,
    fetchDivingData, // Expose refetch function for UI
    apiClient, // Expose apiClient for other hooks if needed (e.g., for share link)
  };
};
